import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { format } from "date-fns";
import { getAllTemplates, getPagedReports, createReport, createSectionForReport, duplicateTemplate } from "../api/api";
import { fetchCoverImageWithCache } from "../components/utils/fetchCoverImageWithCache";
import fi from "date-fns/locale/fi";
import { reportTypeNames } from "../components/utils/reportNameToTypeNormalizer";
import { useUser } from "../context/UserContext";
import Can from "../permissions/Can";
import { PERMISSIONS } from "../permissions/permissions";
import { ReportStateToggle } from "../components/utils/ReportStateToggle";

const TYPE_COLORS = [
  { color: '#1a6b3c', border: '#1a6b3c' }, // deep green
  { color: '#0a5dc2', border: '#0a5dc2' }, // blue
  { color: '#7b3f00', border: '#7b3f00' }, // brown
  { color: '#5a3e8b', border: '#5a3e8b' }, // purple
  { color: '#b05000', border: '#b05000' }, // amber-orange
  { color: '#006b6b', border: '#006b6b' }, // teal
  { color: '#8b1a1a', border: '#8b1a1a' }, // dark red
  { color: '#2d5986', border: '#2d5986' }, // steel blue
];

function typeColor(type) {
  if (!type) return TYPE_COLORS[0];
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) >>> 0;
  return TYPE_COLORS[hash % TYPE_COLORS.length];
}

export const renderStatusBadge = (state) => {
  switch (state) {
    case "ready":
      return <span className="badge bg-success">Valmis</span>;
    case "archived":
      return <span className="badge bg-secondary">Arkistoitu</span>;
    default:
      return <span className="badge bg-warning text-dark">Luonnos</span>;
  }
};

export default function AllReports() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { localUser: user } = useUser();

  const [activeTab, setActiveTab] = useState(() => {
    const t = query.get("tab") || "reports";
    if (t === "myreports" || t === "valmislauseet") return "reports"; // migrate old URLs
    return t;
  });

  // Valmislauseet moved from a tab here to its own page/sidebar entry — an
  // old ?tab=valmislauseet link (bookmarked, or shared before the move)
  // should still land on the right view instead of silently falling back
  // to the Raportit tab above.
  useEffect(() => {
    if (query.get("tab") !== "valmislauseet") return;
    const params = new URLSearchParams();
    for (const key of ["vtype", "vtag", "vchapter"]) {
      const v = query.get(key);
      if (v) params.set(key, v);
    }
    navigate(`/valmislauseet?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sortOrder, setSortOrder] = useState(query.get("sort") || "DESC");
  const [sortBy, setSortBy] = useState(query.get("sortBy") || "updated_at");
  const [pagedReports, setPagedReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(parseInt(query.get("page")) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [reportTypeFilter, setReportTypeFilter] = useState(query.get("type") || "");
const [ownReports, setOwnReports] = useState(() => {
  const saved = localStorage.getItem("reports-ownReports");

  if (saved !== null) {
    return saved === "true";
  }

  return (
    query.get("tab") === "myreports" ||
    query.get("own") === "1"
  );
});

const [showReadyReports, setShowReadyReports] = useState(() => {
  const saved = localStorage.getItem("reports-showReady");

  if (saved !== null) {
    return saved === "true";
  }

  return query.get("ready") === "1";
});

const [showNonReadyReports, setShowNonReadyReports] = useState(() => {
  const saved = localStorage.getItem("reports-showNonReady");

  if (saved !== null) {
    return saved === "true";
  }

  return query.get("nonReady") !== "0";
});

  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateType, setNewTemplateType] = useState('');
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [sourceTemplateId, setSourceTemplateId] = useState(''); // '' = empty pohja, otherwise duplicate this template's id

  const limit = 24;

  // Keep URL in sync with all filter state
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", activeTab);
    if (ownReports) params.set("own", "1");
    if (sortOrder !== "DESC") params.set("sort", sortOrder);
    if (sortBy !== "updated_at") params.set("sortBy", sortBy);
    if (page > 1) params.set("page", String(page));
    if (reportTypeFilter) params.set("type", reportTypeFilter);
    if (showReadyReports) params.set("ready", "1");
    if (!showNonReadyReports) params.set("nonReady", "0");
    navigate(`?${params.toString()}`, { replace: true });
  }, [activeTab, ownReports, sortOrder, sortBy, page, reportTypeFilter, showReadyReports, showNonReadyReports]); // eslint-disable-line react-hooks/exhaustive-deps
useEffect(() => {
  localStorage.setItem(
    "reports-ownReports",
    String(ownReports)
  );

  localStorage.setItem(
    "reports-showReady",
    String(showReadyReports)
  );

  localStorage.setItem(
    "reports-showNonReady",
    String(showNonReadyReports)
  );
}, [
  ownReports,
  showReadyReports,
  showNonReadyReports,
]);
  const getReportUrl = (r) => r.id_kiinteisto
    ? `/taloyhtiokortti/${r.id_kiinteisto}/raportti/${r.type}/${r.id}/${r?.report_sections[0]?.type}/${r?.report_sections[0]?.id}`
    : `/taloyhtiokortti/raportti/${r.type}/${r.id}/${r?.report_sections[0]?.type}/${r?.report_sections[0]?.id}`;

  useEffect(() => {
    if (!user?.id) return;

    const fetchReports = async () => {
      try {
        setLoading(true);

        const res = await getPagedReports({
          page,
          limit,
          sort: sortOrder === "ASC" ? "oldest" : "latest",
          sortBy,
          type: activeTab !== "templates" ? reportTypeFilter || "" : null,
          ownReports,
          showReadyReports: activeTab !== "templates" ? showReadyReports : null,
          showNonReadyReports: activeTab !== "templates" ? showNonReadyReports : null,
        });

        const reportsWithCovers = await Promise.all(
          res.data.data.map(async (r) => {
            if (!r.id_kiinteisto) return r;
            try {
              const coverImageUrl = await fetchCoverImageWithCache(r.id_kiinteisto);
              return { ...r, coverImageUrl };
            } catch {
              return r;
            }
          })
        );

        setPagedReports(reportsWithCovers);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error("Raporttien haku epäonnistui:", err);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === "reports" || activeTab === "myreports") {
      fetchReports();
    }
  }, [user, page, sortOrder, sortBy, activeTab, ownReports, reportTypeFilter, showNonReadyReports, showReadyReports]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await getAllTemplates();
        setTemplates(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTemplates();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setReportTypeFilter("");
    setOwnReports(false);
  };

  const handleOpenReport = (r) => {
    if (r.id_kiinteisto) {
      navigate(`/taloyhtiokortti/${r.id_kiinteisto}/raportti/${r.type}/${r.id}/${r?.report_sections[0]?.type}/${r?.report_sections[0]?.id}`);
    } else {
      navigate(`/taloyhtiokortti/raportti/${r.type}/${r.id}/${r?.report_sections[0]?.type}/${r?.report_sections[0]?.id}`);
    }
  };

  const handleOpenTemplate = (t) => {
    const first = t?.report_sections?.[0];
    if (first) {
      navigate(`/taloyhtiokortti/raportti/${t.type}/${t.id}/${first.type}/${first.id}`);
    } else {
      navigate(`/taloyhtiokortti/raportti/${t.type}/${t.id}`);
    }
  };

  const [templateCreateError, setTemplateCreateError] = useState('');

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateType.trim()) return;
    setTemplateCreateError('');
    try {
      setCreatingTemplate(true);

      let newTemplate, firstSection;

      if (sourceTemplateId) {
        // Duplicate: copies every section (content, order) from the chosen
        // template — so a v2 pass can start as a full copy and only the
        // blocks that actually change need touching.
        const res = await duplicateTemplate({
          sourceTemplateId,
          name: newTemplateName.trim(),
          type: newTemplateType.trim(),
          userId: user.id,
        });
        newTemplate = res.data;
        firstSection = newTemplate.report_sections?.[0];
      } else {
        const res = await createReport({
          name: newTemplateName.trim(),
          type: newTemplateType.trim(),
          template: true,
          created_by: user.id,
        });
        newTemplate = res.data;

        // Create a default first section so the editor route has valid params
        const sectionRes = await createSectionForReport({
          report_id: newTemplate.id,
          userId: user.id,
          data: { name: 'Kansisivu', type: 'kansisivu', content: '', template: false },
        });
        firstSection = sectionRes.data;
      }

      setNewTemplateName('');
      setNewTemplateType('');
      setSourceTemplateId('');
      setShowNewTemplateForm(false);

      if (firstSection) {
        navigate(`/taloyhtiokortti/raportti/${newTemplate.type}/${newTemplate.id}/${firstSection.type}/${firstSection.id}`);
      } else {
        navigate(`/taloyhtiokortti/raportti/${newTemplate.type}/${newTemplate.id}`);
      }
    } catch (err) {
      console.error('Pohjan luonti epäonnistui:', err);
      setTemplateCreateError(err?.response?.data?.error || err?.response?.data?.message || 'Pohjan luonti epäonnistui.');
    } finally {
      setCreatingTemplate(false);
    }
  };

  if (!user) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border text-success" />
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="bg-success text-white text-center py-4 shadow-sm">
        <h1 className="fw-bold fs-4 display-5 mb-1">Raportit ja pohjat</h1>
        <p className="opacity-75 mb-0">Tarkastele raportteja ja muokkaa pohjia</p>
      </div>

      <div className="container py-4">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3 justify-content-center" style={{ fontSize: 'clamp(0.72rem, 2.5vw, 0.95rem)' }}>
          <li className="nav-item">
            <button
              className={`nav-link px-2 px-md-3 py-1 ${activeTab === "reports" ? "active" : ""}`}
              onClick={() => handleTabChange("reports")}
            >
              Raportit
            </button>
          </li>
          <Can permission={PERMISSIONS.REPORTS.VIEW_TEMPLATES}>
            <li className="nav-item">
              <button
                className={`nav-link px-2 px-md-3 py-1 ${activeTab === "templates" ? "active" : ""}`}
                onClick={() => handleTabChange("templates")}
              >
                Raporttipohjat
              </button>
            </li>
          </Can>
        </ul>

        {/* Filters */}
        {activeTab !== "templates" && (
          <div className="mb-3 d-flex flex-wrap gap-2 justify-content-center">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: 140 }}
              value={reportTypeFilter}
              onChange={e => { setReportTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="">Kaikki raportit</option>
              {Object.values(
                templates.filter(t => t.type && t.name).reduce((acc, t) => {
                  if (!acc[t.type]) acc[t.type] = t;
                  return acc;
                }, {})
              ).sort((a, b) => a.name.localeCompare(b.name, 'fi')).map(t => (
                <option key={t.type} value={t.type}>
                  {t.name}
                </option>
              ))}
            </select>

            <div className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ backgroundColor: '#f0f4ff', borderColor: '#b0c4de' }}>
              <span className="text-muted me-1" style={{ fontSize: '0.72rem' }}>Järjestys:</span>
              {['updated_at', 'field_date'].map((field) => {
                const isActive = sortBy === field;
                const label = field === 'updated_at' ? 'Muokattu' : 'Kenttäpvm';
                const icon = isActive ? (sortOrder === 'DESC' ? ' ↓' : ' ↑') : '';
                return (
                  <button
                    key={field}
                    className={`btn btn-sm ${isActive ? 'btn-success' : 'btn-outline-success'}`}
                    style={{ fontSize: '0.75rem' }}
                    onClick={() => {
                      if (isActive) {
                        setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
                      } else {
                        setSortBy(field);
                        setSortOrder('DESC');
                      }
                      setPage(1);
                    }}
                  >
                    {label}{icon}
                  </button>
                );
              })}
            </div>

            <div className="d-flex align-items-center gap-1 form-check form-switch m-0">
              <input
                className="form-check-input mt-0"
                type="checkbox"
                id="switch-luonnokset"
                checked={showNonReadyReports}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setShowNonReadyReports(newValue);
                  if (!newValue && !showReadyReports) setShowReadyReports(true);
                }}
              />
              <label className="form-check-label" htmlFor="switch-luonnokset">Luonnokset</label>
            </div>

            <div className="d-flex align-items-center gap-1 form-check form-switch m-0">
              <input
                className="form-check-input mt-0"
                type="checkbox"
                id="switch-valmiit"
                checked={showReadyReports}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setShowReadyReports(newValue);
                  if (!newValue && !showNonReadyReports) setShowNonReadyReports(true);
                }}
              />
              <label className="form-check-label" htmlFor="switch-valmiit">Valmiit</label>
            </div>

            <Can permission={PERMISSIONS.REPORTS.VIEW_ALL}>
              <div className="d-flex align-items-center gap-1 form-check form-switch m-0">
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  id="switch-omat"
                  checked={ownReports}
                  onChange={(e) => { setOwnReports(e.target.checked); setPage(1); }}
                />
                <label className="form-check-label" htmlFor="switch-omat">Vain omat</label>
              </div>
            </Can>
          </div>
        )}

        {/* Reports Grid */}
        {activeTab === "reports" && (
          <>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" />
              </div>
            ) : pagedReports.length === 0 ? (
              <p>{activeTab === "reports" ? "Ei raportteja saatavilla." : "Sinulle ei ole vielä liitetty raportteja."}</p>
            ) : (
              <div className="border rounded overflow-hidden">
                {pagedReports.map((r, i) => {
                  const mainRakennus = r.kiinteisto?.rakennukset_fulls?.[0];
                  const msPerDay = 24 * 60 * 60 * 1000;
                  const daysSince = r.field_date ? Math.floor((Date.now() - new Date(r.field_date).getTime()) / msPerDay) : null;
                  const daysLeft = daysSince !== null ? 30 - daysSince : null;
                  const fieldColor = daysLeft === null ? '#6c757d'
                    : daysLeft > 14 ? '#04aa00'
                    : daysLeft > 7  ? '#d0c407'
                    : daysLeft >= 0 ? '#ba3b46'
                    : '#111';
                  return (
                    <Link
                      key={r.id}
                      to={getReportUrl(r)}
                      className="text-decoration-none text-reset d-flex align-items-center gap-2 gap-sm-3 px-2 px-sm-3 py-2"
                      style={{
                        borderTop: i > 0 ? '1px solid #e9ecef' : 'none',
                        minHeight: 64,
                        backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0faf4'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#fafafa'}
                    >
                      {/* Thumbnail — desktop only */}
                      <div className="flex-shrink-0 rounded overflow-hidden d-none d-sm-flex align-items-center justify-content-center"
                        style={{
                          width: 120, height: 90,
                          backgroundImage: r.coverImageUrl ? `url(${r.coverImageUrl})` : 'none',
                          backgroundSize: 'cover', backgroundPosition: 'center',
                          backgroundColor: '#e9ecef',
                        }}>
                        {!r.coverImageUrl && <i className="bi bi-building text-secondary" style={{ fontSize: '2rem' }}></i>}
                      </div>

                      {/* Main info */}
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold text-truncate" style={{ fontSize: '1rem' }}>
                          {r.kiinteisto?.kohteen_nimi || r.name}
                        </div>
                        <div className="text-muted text-truncate" style={{ fontSize: '0.82rem' }}>
                          {[mainRakennus?.osoite, mainRakennus?.toimipaikka].filter(Boolean).join(', ') || '–'}
                        </div>
                        {/* Date shown inline on mobile */}
                        {r.field_date && (
                          <div className="d-sm-none" style={{ fontSize: '0.75rem', color: fieldColor, fontWeight: daysLeft !== null && daysLeft <= 14 ? 700 : 400 }}>
                            {format(new Date(r.field_date), "d.M.yyyy", { locale: fi })}
                            {daysLeft !== null && (
                              <span style={{
                                marginLeft: 5, fontSize: '0.7rem', fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                                display: 'inline-block', verticalAlign: 'middle',
                                color: daysLeft < 0 ? '#fff' : daysLeft <= 7 ? '#fff' : daysLeft <= 14 ? '#5a5200' : '#fff',
                                background: daysLeft < 0 ? '#111' : daysLeft <= 7 ? '#ba3b46' : daysLeft <= 14 ? '#d0c407' : '#04aa00',
                              }}>
                                {daysLeft > 0 ? `${daysLeft} pv` : daysLeft === 0 ? 'Tänään' : `+${Math.abs(daysLeft)} pv`}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="mt-1">
                          {(() => { const tc = typeColor(r.type); return (
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: tc.color, border: `1.5px solid ${tc.border}`, borderRadius: 4, padding: '1px 7px', display: 'inline-block' }}>
                              {r.name || r.type}
                            </span>
                          ); })()}
                        </div>
                      </div>

                      {/* Field date — desktop only */}
                      <div className="flex-shrink-0 text-end d-none d-sm-block" style={{ minWidth: 120 }}>
                        <div style={{ fontSize: '1rem', color: fieldColor, fontWeight: daysLeft !== null && daysLeft <= 14 ? 700 : 500 }}>
                          {r.field_date ? format(new Date(r.field_date), "d.M.yyyy", { locale: fi }) : '–'}
                        </div>
                        {daysLeft !== null && (
                          <div style={{
                            fontSize: '0.85rem', fontWeight: 700,
                            color: daysLeft < 0 ? '#fff' : daysLeft <= 7 ? '#fff' : daysLeft <= 14 ? '#5a5200' : '#fff',
                            marginTop: 3, padding: '2px 9px', borderRadius: 10,
                            display: 'inline-block',
                            background: daysLeft < 0 ? '#111' : daysLeft <= 7 ? '#ba3b46' : daysLeft <= 14 ? '#d0c407' : '#04aa00',
                          }}>
                            {daysLeft > 0 ? `${daysLeft} pv jäljellä` : daysLeft === 0 ? 'Tänään' : `+${Math.abs(daysLeft)} pv`}
                          </div>
                        )}
                        <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                          {r.updated_at ? format(new Date(r.updated_at), "d.M.yyyy", { locale: fi }) : ''}
                        </div>
                      </div>

                      {/* State toggle — stop propagation so click doesn't navigate */}
                      <div className="flex-shrink-0" onClick={e => e.preventDefault()}>
                        <ReportStateToggle
                          report={r}
                          onStateChange={(id, newState) =>
                            setPagedReports(prev => prev.map(rep => rep.id === id ? { ...rep, state: newState } : rep))
                          }
                          requireConfirm={true}
                        />
                      </div>

                      <i className="bi bi-chevron-right text-muted flex-shrink-0" style={{ fontSize: '0.85rem' }}></i>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label="Page navigation" className="d-flex justify-content-center my-3">
                <ul className="pagination shadow-sm rounded">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link text-success" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      Edellinen
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">{page} / {totalPages}</span>
                  </li>
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button className="page-link text-success" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      Seuraava
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}

        {/* Templates Tab */}
        {activeTab === "templates" && (
          <div>
            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-success btn-sm"
                onClick={() => setShowNewTemplateForm((v) => !v)}
              >
                {showNewTemplateForm ? 'Peruuta' : '+ Luo uusi pohja'}
              </button>
            </div>

            {showNewTemplateForm && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <h6 className="card-title mb-3">Uusi raporttipohja</h6>
                  <div className="mb-3">
                    <label className="form-label form-label-sm mb-1">Pohjana käytetään</label>
                    <select
                      className="form-select form-select-sm"
                      value={sourceTemplateId}
                      onChange={(e) => setSourceTemplateId(e.target.value)}
                    >
                      <option value="">— Tyhjä pohja —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} ({t.type})</option>
                      ))}
                    </select>
                    <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                      {sourceTemplateId
                        ? 'Kaikki valitun pohjan osiot kopioidaan uuteen pohjaan — vaihda vain tarvittavat lohkot.'
                        : 'Tyhjä pohja luo vain kansisivun, loput lisätään käsin.'}
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label form-label-sm mb-1">Nimi</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="esim. KUNTOARVIO WK1"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label form-label-sm mb-1">Tyyppi</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="esim. kuntoarvio_wk1_v2"
                      value={newTemplateType}
                      onChange={(e) => setNewTemplateType(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateTemplate()}
                    />
                    <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                      Olemassa olevat tyypit: wpts, kuntoarvio_wk1, markatila, wk3, 10_vuotistarkastus, Ikkunoiden_ja_Ulko-ovien_Kuntoarvio — täytyy olla uusi, ei jo käytössä.
                    </div>
                  </div>
                  {templateCreateError && (
                    <div className="alert alert-danger py-1 px-2 mb-3" style={{ fontSize: '0.78rem' }}>{templateCreateError}</div>
                  )}
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleCreateTemplate}
                    disabled={creatingTemplate || !newTemplateName.trim() || !newTemplateType.trim()}
                  >
                    {creatingTemplate ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    {sourceTemplateId ? 'Kopioi pohja' : 'Luo pohja'}
                  </button>
                </div>
              </div>
            )}

            {templates.length === 0 ? (
              <p className="text-muted">Ei pohjia saatavilla.</p>
            ) : (
              <div className="row">
                {templates.map((t) => (
                  <div key={t.id} className="col-12 col-sm-6 col-lg-4 mb-3">
                    <div className="card shadow-sm border-0">
                      <div className="card-body">
                        <h5 className="card-title" style={{ wordBreak: 'break-word' }}>{t.name}</h5>
                        <p className="text-muted small mb-1">{t.type}</p>
                        <Link
                          to={t?.report_sections?.[0]
                            ? `/taloyhtiokortti/raportti/${t.type}/${t.id}/${t.report_sections[0].type}/${t.report_sections[0].id}`
                            : `/taloyhtiokortti/raportti/${t.type}/${t.id}`}
                          className="btn btn-outline-primary w-100 mt-2"
                        >
                          Avaa pohja
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
